import { io } from 'socket.io-client';
import { getAuthToken } from './auth';
import { getApiUrl } from '../config/urls';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionPromise = null;
    this.currentToken = null;
    this.defaultListenersBound = false;
    this.negotiationSocket = null;
    this.negotiationConnectionPromise = null;
    this.negotiationListeners = new Map();
    this.negotiationUpdateBound = false;
    // Retirer le /api de l'URL de base
    const apiUrl = getApiUrl().replace(/\/api\/?$/, '');
    this.baseUrl = apiUrl.replace(/\/api$/, '');
    this.eventListeners = new Map();
    this.forwarders = new Map();
    this.chatBridgeBound = false;
  }

  _normalizeBaseUrl(url) {
    if (!url) return this.baseUrl;
    return String(url).replace(/\/api\/?$/, '').replace(/\/+$/, '');
  }

  /** Sur localhost, désactiver l’upgrade WS évite « Invalid frame header » (proxy CRA, extensions, intercepteurs). */
  _shouldUpgradeTransport(baseUrl) {
    const u = this._normalizeBaseUrl(baseUrl);
    if (process.env.REACT_APP_SOCKETIO_UPGRADE === '0') return false;
    if (process.env.REACT_APP_SOCKETIO_UPGRADE === '1') return true;
    return !/(localhost|127\.0\.0\.1)/i.test(u);
  }

  _emitEvent(event, data) {
    const listeners = this.eventListeners.get(event) || [];
    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        console.error(`Error in ${event} listener:`, error);
      }
    }
  }

  _bindEventToSocket(event) {
    if (!this.socket) return;
    const listeners = this.eventListeners.get(event) || [];
    if (listeners.length === 0) return;
    if (this.forwarders.has(event)) return;
    const forwarder = (data) => this._emitEvent(event, data);
    this.forwarders.set(event, forwarder);
    this.socket.on(event, forwarder);
  }

  _rebindRegisteredEvents() {
    if (!this.socket) return;
    for (const event of this.eventListeners.keys()) {
      this._bindEventToSocket(event);
    }
  }

  connect(url) {
    if (url) {
      this.baseUrl = this._normalizeBaseUrl(url);
    }

    const token = getAuthToken();
    if (!token) {
      return Promise.reject(new Error('No authentication token found'));
    }

    if (this.socket && this.socket.connected && this.currentToken === token) {
      return Promise.resolve(this.socket);
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.socket && this.currentToken === token) {
      this.connectionPromise = new Promise((resolve, reject) => {
        const handleConnect = () => {
          this.socket.off('connect_error', handleConnectError);
          this.isConnected = true;
          this.connectionPromise = null;
          resolve(this.socket);
        };
        const handleConnectError = (error) => {
          this.socket.off('connect', handleConnect);
          this.connectionPromise = null;
          reject(error);
        };
        this.socket.once('connect', handleConnect);
        this.socket.once('connect_error', handleConnectError);
        this.socket.auth = { token, clientPlatform: 'web' };
        this.socket.connect();
      });
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        if (this.socket && this.currentToken !== token) {
          this.disconnect();
        }
        this.currentToken = token;

        console.log('🔌 Connecting to Socket.IO at:', this.baseUrl);
        
        // Configuration optimisée pour la connexion
        const socketOptions = {
          path: '/socket.io/',
          // Configuration du transport
          transports: ['polling', 'websocket'],
          upgrade: this._shouldUpgradeTransport(this.baseUrl),
          // Désactiver les mécanismes de compatibilité hérités
          allowEIO3: false,
          // Activer le mode debug en développement
          debug: process.env.NODE_ENV === 'development',
          // Activer les credentials pour les cookies d'authentification
          withCredentials: true,
          // Configuration de la reconnexion
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          // Timeout de connexion
          timeout: 10000,
          // Réutiliser une même connexion côté client
          forceNew: false,
          multiplex: true,
          // Configuration des en-têtes
          extraHeaders: {
            'Authorization': `Bearer ${token}`
          },
          // Paramètres de requête
          query: {
            token: token,
            clientPlatform: 'web',
          },
          auth: {
            token,
            clientPlatform: 'web',
          },
        };
        
        console.log('🔌 Socket.IO connection options:', JSON.stringify(socketOptions, null, 2));
        
        this.socket = io(this.baseUrl, socketOptions);
        
        // Log socket connection details
        console.log('Socket.IO connection details:', {
          connected: this.socket.connected,
          id: this.socket.id,
          io: {
            uri: this.socket.io.uri,
            opts: this.socket.io.opts
          }
        });

        // Connection established
        this.socket.on('connect', () => {
          console.log('✅ Connected to Socket.IO server');
          this.isConnected = true;
          this.connectionPromise = null;
          this._emitEvent('connect');
          this._emitEvent('message', { type: 'connect' });
          this._rebindRegisteredEvents();
          this._setupEventListeners();
          resolve(this.socket);
        });

        // Connection error with detailed logging
        this.socket.on('connect_error', (error) => {
          console.error('❌ Socket.IO connection error:', {
            message: error.message,
            description: error.description,
            context: error.context,
            type: error.type,
            stack: error.stack
          });
          
          // Log additional connection details
          if (this.socket) {
            console.error('Socket state:', {
              connected: this.socket.connected,
              id: this.socket.id,
              io: {
                uri: this.socket.io?.uri,
                opts: this.socket.io?.opts
              }
            });
          }
          
          this._emitEvent('connect_error', error);
          this._handleError(error, reject);
        });

        // Disconnected
        this.socket.on('disconnect', (reason) => {
          console.log('🔌 Disconnected from Socket.IO server:', reason);
          this.isConnected = false;
          this.connectionPromise = null;
          this._emitEvent('disconnect', reason);
        });

      } catch (error) {
        this._handleError(error, reject);
      }
    });

    return this.connectionPromise;
  }

  disconnect() {
    if (this.socket) {
      for (const [event, forwarder] of this.forwarders.entries()) {
        this.socket.off(event, forwarder);
      }
      this.forwarders.clear();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.connectionPromise = null;
    this.currentToken = null;
    this.defaultListenersBound = false;
  }

  _setupEventListeners() {
    if (!this.socket || this.defaultListenersBound) return;
    this.defaultListenersBound = true;
    // Setup any default event listeners here
    this.socket.on('new_message', (message) => {
      console.log('New message received:', message);
      this._emitEvent('new_message', message);
    });

    this.socket.on('user_connected', (userId) => {
      console.log('User connected:', userId);
      this._emitEvent('user_connected', userId);
    });

    this.socket.on('user_disconnected', (userId) => {
      console.log('User disconnected:', userId);
      this._emitEvent('user_disconnected', userId);
    });

    this.socket.on('admin:negotiation_update', (payload) => {
      this._emitEvent('admin:negotiation_update', payload);
    });

    this.socket.on('admin:referral_update', (payload) => {
      this._emitEvent('admin:referral_update', payload);
    });

    this.socket.on('admin:user_presence', (payload) => {
      this._emitEvent('admin:user_presence', payload);
    });
  }

  _handleError(error, reject) {
    console.error('Socket.IO error:', error);
    this.disconnect();
    if (reject) {
      reject(error);
    }
    this._emitEvent('error', error);
  }

  // Public API
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
    if (this.socket) {
      this._bindEventToSocket(event);
    }
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
      if (listeners.length === 0) {
        this.eventListeners.delete(event);
        const forwarder = this.forwarders.get(event);
        if (forwarder && this.socket) {
          this.socket.off(event, forwarder);
        }
        this.forwarders.delete(event);
      }
    }
  }

  emit(event, data) {
    if (!this.socket) {
      console.error('Socket is not connected');
      return Promise.reject(new Error('Socket is not connected'));
    }
    return new Promise((resolve, reject) => {
      this.socket.emit(event, data, (response) => {
        if (response && response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }

  send(data) {
    if (!this.socket) {
      console.error('Socket is not connected');
      return false;
    }
    if (data && typeof data === 'object' && data.event) {
      this.socket.emit(data.event, data.payload ?? data.data ?? {});
      return true;
    }
    this.socket.emit('message', data);
    return true;
  }

  getRawSocket() {
    return this.socket;
  }

  isSocketConnected() {
    return !!(this.socket && this.socket.connected);
  }

  _negotiationSocketOptions(token) {
    const negotiationUrl = `${this._normalizeBaseUrl(this.baseUrl)}/negotiation`;
    const negotiationUpgrade =
      process.env.REACT_APP_SOCKETIO_UPGRADE === '0'
        ? false
        : process.env.REACT_APP_SOCKETIO_UPGRADE === '1'
          ? true
          : !/(localhost|127\.0\.0\.1)/i.test(negotiationUrl);
    return {
      url: negotiationUrl,
      options: {
        path: '/socket.io/',
        query: { token },
        transports: ['polling', 'websocket'],
        upgrade: negotiationUpgrade,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        auth: { token, clientPlatform: 'web' },
      },
    };
  }

  _ensureNegotiationSocket(token) {
    if (this.negotiationSocket?.connected && this.currentToken === token) {
      return Promise.resolve(this.negotiationSocket);
    }
    if (this.negotiationConnectionPromise) {
      return this.negotiationConnectionPromise;
    }

    const { url, options } = this._negotiationSocketOptions(token);
    this.currentToken = token;
    this.negotiationConnectionPromise = new Promise((resolve, reject) => {
      if (this.negotiationSocket) {
        this.negotiationSocket.removeAllListeners();
        this.negotiationSocket.disconnect();
        this.negotiationSocket = null;
      }

      const socket = io(url, options);
      this.negotiationSocket = socket;

      const onConnect = () => {
        socket.off('connect_error', onError);
        this.negotiationConnectionPromise = null;
        if (!this.negotiationUpdateBound) {
          this.negotiationUpdateBound = true;
          socket.on('negotiation:update', (data) => {
            const quoteId = data?.quoteId || data?.quote?.id;
            if (quoteId) {
              const listeners = this.negotiationListeners.get(String(quoteId));
              if (listeners) {
                listeners.forEach((fn) => {
                  try {
                    fn(data);
                  } catch (e) {
                    console.error('[socketService] negotiation listener error:', e);
                  }
                });
              }
              return;
            }
            for (const listeners of this.negotiationListeners.values()) {
              listeners.forEach((fn) => {
                try {
                  fn(data);
                } catch (e) {
                  console.error('[socketService] negotiation listener error:', e);
                }
              });
            }
          });
        }
        for (const quoteId of this.negotiationListeners.keys()) {
          socket.emit('joinQuoteRoom', { quoteId }, () => {});
        }
        resolve(socket);
      };

      const onError = (error) => {
        socket.off('connect', onConnect);
        this.negotiationConnectionPromise = null;
        reject(error);
      };

      socket.once('connect', onConnect);
      socket.once('connect_error', onError);
    });

    return this.negotiationConnectionPromise;
  }

  /**
   * Namespace /negotiation partagé (une seule connexion pour tous les écrans devis).
   */
  subscribeNegotiation(quoteId, onUpdate) {
    if (!quoteId || typeof onUpdate !== 'function') {
      return () => {};
    }
    const token = getAuthToken();
    if (!token) {
      return () => {};
    }

    const key = String(quoteId);
    if (!this.negotiationListeners.has(key)) {
      this.negotiationListeners.set(key, new Set());
    }
    this.negotiationListeners.get(key).add(onUpdate);

    this._ensureNegotiationSocket(token)
      .then((socket) => {
        socket.emit('joinQuoteRoom', { quoteId: key }, () => {});
      })
      .catch((err) => {
        console.error('[socketService] negotiation connect failed:', err?.message || err);
      });

    return () => {
      const listeners = this.negotiationListeners.get(key);
      if (!listeners) return;
      listeners.delete(onUpdate);
      if (listeners.size === 0) {
        this.negotiationListeners.delete(key);
        if (this.negotiationSocket?.connected) {
          try {
            this.negotiationSocket.leave(key);
          } catch (_) {
            /* ignore */
          }
        }
      }
      if (this.negotiationListeners.size === 0 && this.negotiationSocket) {
        this.negotiationSocket.disconnect();
        this.negotiationSocket = null;
        this.negotiationUpdateBound = false;
        this.negotiationConnectionPromise = null;
      }
    };
  }

  /** Pont chat : branche les événements chat sur le socket principal partagé. */
  bindChatBridge(handlers = {}) {
    this._chatHandlers = handlers;
    return this.connect().then(() => {
      if (!this.socket) return null;
      if (this.chatBridgeBound) return this.socket;
      this.chatBridgeBound = true;
      this._chatForwarders = {
        new_message: (data) => this._chatHandlers?.onNewMessage?.(data),
        message_sent: (data) => this._chatHandlers?.onMessageSent?.(data),
        user_typing: (data) => this._chatHandlers?.onTyping?.(data),
        messages_read: (data) => this._chatHandlers?.onMessagesRead?.(data),
      };
      Object.entries(this._chatForwarders).forEach(([event, forwarder]) => {
        this.socket.on(event, forwarder);
      });
      return this.socket;
    });
  }

  releaseChatBridge() {
    if (this.socket && this._chatForwarders) {
      Object.entries(this._chatForwarders).forEach(([event, forwarder]) => {
        this.socket.off(event, forwarder);
      });
    }
    this._chatForwarders = null;
    this._chatHandlers = null;
    this.chatBridgeBound = false;
  }
}

// Export a singleton instance
export const socketService = new SocketService();

export default socketService;
